import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { Table } from "react-bootstrap";
import TablePaginationBar from "../components/TablePaginationBar";
import useFetchData from "../hooks/useFetchData";
import { config } from "../config/config";

const JourneyExercisesView = () => {
  const { id } = useParams();
  const location = useLocation();
  const journey = location.state?.journey;
  const navigate = useNavigate();
  const user = useSelector((state) => state?.user);

  const [currentPage, setCurrentPage] = useState(1);
  const [exercises, setExercises] = useState([]);
  const [currentExercises, setCurrentExercises] = useState([]);
  const [exercisesPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [journeyDetails, setJourneyDetails] = useState(journey || {});

  const businessId = user.user?.businessId;
  const businessIdentifier = user.user?.businessIdentifier;

  // Fetch journey details if not provided in location state
  useEffect(() => {
    const fetchJourneyDetails = async () => {
      if (!journey && id) {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_BASE_URL}/futureSelf/${id}`,
            config
          );
          if (response.status === 200) {
            setJourneyDetails(response.data);
          }
        } catch (error) {
          console.error("Error fetching journey details:", error);
          toast.error("Failed to load journey details");
        }
      }
    };

    fetchJourneyDetails();
  }, [id, journey, businessId, businessIdentifier]);

  // Fetch exercises for this journey
  useEffect(() => {
    const fetchExercises = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/futureSelf/${id}/exercises`,
          config
        );
        if (response.status === 200) {
          setExercises(response.data || []);
        }
      } catch (error) {
        console.error("Error fetching exercises:", error);
        toast.error("Failed to load exercises");
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchExercises();
    }
  }, [id, businessId, businessIdentifier]);

  // Handle pagination
  useEffect(() => {
    if (exercises.length > 0) {
      const indexOfLastExercise = currentPage * exercisesPerPage;
      const indexOfFirstExercise = indexOfLastExercise - exercisesPerPage;
      setCurrentExercises(exercises.slice(indexOfFirstExercise, indexOfLastExercise));
    }
  }, [currentPage, exercises, exercisesPerPage]);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleAddExercise = () => {
    navigate(`/journeys/${id}/exercises/add`, {
      state: { journeyId: id, journeyName: journeyDetails.name }
    });
  };

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <button 
          className="btn btn-outline-secondary"
          onClick={() => navigate("/journeys")}
        >
          &lt; Back to Journeys
        </button>
      </div>
      
      <div className="bg-white rounded shadow-sm mb-4">
        <div className="p-4">
          <h2 className="app-page-title">
            Journey Details
          </h2>
          
          <div className="row mt-3">
            <div className="col-md-6">
              <p><strong>Name:</strong> {journeyDetails.name}</p>
              <p><strong>Tag:</strong> {journeyDetails.tag}</p>
            </div>
            <div className="col-md-6">
              <p><strong>Number of Exercises:</strong> {exercises.length}</p>
              <p><strong>Status:</strong> {journeyDetails.status}</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded shadow-sm">
        <div className="app-page-toolbar">
          <h2 className="app-page-title">Journey Exercises</h2>
          <div className="app-page-toolbar-filters">
            <button
              className="app-filter-action-btn"
              onClick={handleAddExercise}
            >
              Add New
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="text-center p-4">Loading Exercises...</div>
        ) : exercises.length === 0 ? (
          <div className="text-center p-4">No Exercises available for this journey.</div>
        ) : (
          <>
            <Table
              className="mb-0"
              hover
              responsive
              style={{ borderCollapse: "collapse", borderSpacing: 0 }}
            >
              <thead
                style={{
                  backgroundColor: "#064260",
                  color: "white",
                  height: "50px",
                  textAlign: "center",
                }}
              >
                <tr>
                  <th>ID</th>
                  <th>Exercise</th>
                  <th style={{width: "160px"}}>Action</th>
                </tr>
              </thead>
              <tbody
                style={{
                  color: "#575757",
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  fontWeight: 500,
                  textAlign: "center",
                }}
              >
                {currentExercises.map((exercise, index) => (
                  <tr key={exercise.id} style={{ backgroundColor: "white" }}>
                    <td style={{verticalAlign: "middle"}}>
                      {(index + 1 + (currentPage - 1) * exercisesPerPage)
                        .toString()
                        .padStart(4, "0")}
                    </td>
                    <td style={{verticalAlign: "middle"}}>{exercise.exercise || exercise.name}</td>
                    <td style={{width: "160px"}} className="text-center">
                      <button
                        className="app-view-btn"
                        onClick={() =>
                          navigate(`/journeys/${id}/exercises/${exercise.id}`, {
                            state: { exercise, journeyId: id },
                          })
                        }
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <TablePaginationBar
              currentPage={currentPage}
              totalItems={exercises.length}
              rowsPerPage={exercisesPerPage}
              onPageChange={paginate}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default JourneyExercisesView;